/**
 * React Query bindings for the appointments mock API.
 *
 * Cache keys:
 *   - ['appointments', 'staff']
 *   - ['appointments', 'week', isoMonday]
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  cancelAppointment,
  createCashierOrderFromAppointment,
  createAppointment,
  editAppointment,
  listAppointments,
  listCustomers,
  listPets,
  listServices,
  listStaff,
  updateAppointment,
} from './appointments-api';
import type {
  AppointmentRecord,
  CancelAppointmentPayload,
  CreateCashierOrderFromAppointmentPayload,
  CreateCashierOrderFromAppointmentResult,
  CreateAppointmentPayload,
  EditAppointmentPayload,
  UpdateAppointmentPayload,
} from './types';

export const appointmentKeys = {
  all: ['appointments'] as const,
  staff: () => [...appointmentKeys.all, 'staff'] as const,
  customers: () => [...appointmentKeys.all, 'customers'] as const,
  pets: (customerId?: string) =>
    [...appointmentKeys.all, 'pets', customerId ?? '__all__'] as const,
  services: () => [...appointmentKeys.all, 'services'] as const,
  week: (isoMonday: string) =>
    [...appointmentKeys.all, 'week', isoMonday] as const,
};

export function useStaffList() {
  return useQuery({
    queryKey: appointmentKeys.staff(),
    queryFn: listStaff,
    staleTime: 5 * 60_000,
  });
}

export function useWeekAppointments(isoMonday: string) {
  return useQuery({
    queryKey: appointmentKeys.week(isoMonday),
    queryFn: () => listAppointments({ weekStart: isoMonday }),
    staleTime: 30_000,
  });
}

export function useUpdateAppointment(isoMonday: string) {
  const qc = useQueryClient();
  return useMutation<
    AppointmentRecord,
    Error,
    UpdateAppointmentPayload,
    { previous: AppointmentRecord[] | undefined }
  >({
    mutationFn: updateAppointment,
    onMutate: async payload => {
      await qc.cancelQueries({ queryKey: appointmentKeys.week(isoMonday) });
      const previous = qc.getQueryData<AppointmentRecord[]>(
        appointmentKeys.week(isoMonday),
      );
      if (previous) {
        qc.setQueryData<AppointmentRecord[]>(
          appointmentKeys.week(isoMonday),
          previous.map(a =>
            a.id === payload.id ? { ...a, ...payload.changes } : a,
          ),
        );
      }
      return { previous };
    },
    onError: (_err, _payload, context) => {
      if (context?.previous) {
        qc.setQueryData(appointmentKeys.week(isoMonday), context.previous);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: appointmentKeys.week(isoMonday) });
    },
  });
}

export function useCancelAppointment(isoMonday: string) {
  const qc = useQueryClient();
  return useMutation<AppointmentRecord, Error, CancelAppointmentPayload>({
    mutationFn: cancelAppointment,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: appointmentKeys.week(isoMonday) });
    },
  });
}

export function useCreateCashierOrderFromAppointment() {
  const qc = useQueryClient();
  return useMutation<
    CreateCashierOrderFromAppointmentResult,
    Error,
    CreateCashierOrderFromAppointmentPayload
  >({
    mutationFn: createCashierOrderFromAppointment,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cashier'] });
    },
  });
}

export function useCreateAppointment(isoMonday: string) {
  const qc = useQueryClient();
  return useMutation<AppointmentRecord, Error, CreateAppointmentPayload>({
    mutationFn: createAppointment,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: appointmentKeys.week(isoMonday) });
    },
  });
}

export function useEditAppointment(isoMonday: string) {
  const qc = useQueryClient();
  return useMutation<AppointmentRecord, Error, EditAppointmentPayload>({
    mutationFn: editAppointment,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: appointmentKeys.week(isoMonday) });
    },
  });
}

export function useCustomers() {
  return useQuery({
    queryKey: appointmentKeys.customers(),
    queryFn: listCustomers,
    staleTime: 5 * 60_000,
  });
}

export function usePets(customerId: string | undefined) {
  return useQuery({
    queryKey: appointmentKeys.pets(customerId),
    queryFn: () => listPets(customerId),
    staleTime: 5 * 60_000,
    enabled: Boolean(customerId),
  });
}

export function useServices() {
  return useQuery({
    queryKey: appointmentKeys.services(),
    queryFn: listServices,
    staleTime: 5 * 60_000,
  });
}
